<!--
  StringInput.vue

  Form input for string-kind fields. Two render modes:

  - Default (`multiline: false`): single-line text `<input>`.
  - Multiline (`multiline: true`): `<textarea>` that grows with content.
    Used by ValueCell's inline editor so long string values aren't trapped
    in a horizontally-scrolling single-line input.

    A textarea has no content-based intrinsic height — it is sized by `rows`
    — so the growing is ours to do: `autosize` below measures the content and
    writes the height. Without it the editor opens one row tall whatever the
    value's length, and (with `resize: none` in the grid overlay) there is no
    way for the reader to open it either.
-->
<template>
  <textarea
    v-if="multiline"
    ref="textareaRef"
    class="textarea"
    :class="`is-${size}`"
    rows="1"
    :value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autofocus="autofocus"
    @input="onMultilineInput"
  />
  <input
    v-else
    class="input"
    :class="`is-${size}`"
    type="text"
    :value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autofocus="autofocus"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue"
import type { Field } from "@auger/core/fields"
import { useField } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    field: Field
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    autofocus?: boolean
    multiline?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    autofocus: false,
    multiline: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput, displayed } = useField(props.field, emit)

const formattedValue = computed(() => displayed(props.modelValue) as string)

// --- Autosize (multiline only) --------------------------------------------

const textareaRef = ref<HTMLTextAreaElement | null>(null)

/**
 * Match the textarea's height to its content.
 *
 * Collapse first so the measurement shrinks as well as grows — `scrollHeight`
 * never reports less than the current height. `scrollHeight` covers content +
 * padding but not borders, so add them back: inside a cell overlay the shared
 * reset strips both to zero and the term falls out, but in a record form the
 * textarea keeps its chrome and would otherwise open one border short of its
 * content and show a scrollbar.
 *
 * A `max-height` (the grid overlay sets 40vh) still wins over the height we
 * write, so an essay-length value scrolls in place instead of filling the
 * screen.
 */
function autosize() {
  const el = textareaRef.value
  if (!el) return
  el.style.height = "auto"
  const borders = el.offsetHeight - el.clientHeight
  el.style.height = `${el.scrollHeight + borders}px`
}

function onMultilineInput(event: Event) {
  handleInput(event)
  autosize()
}

onMounted(() => {
  if (props.multiline) autosize()
})

// Covers every path that changes the text without an input event: the grid's
// type-to-edit seed, a draft restored on re-open, a programmatic write. Post-
// flush so the textarea already holds the new value when it's measured.
watch(
  [formattedValue, () => props.multiline],
  () => {
    if (props.multiline) nextTick(autosize)
  },
  { flush: "post" }
)
</script>
